import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { getProducts } from '@/lib/api';
import { formatPrice, getImageUrl } from '@/lib/utils';

export function SearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getProducts({ search: query, limit: 10 });
        setResults(res.data);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    onClose();
    setQuery('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="sr-only">Search Products</DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for shawls..."
            className="pl-10 pr-10 h-12 text-lg border-stone-300"
            autoFocus
            data-testid="search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto mt-4" data-testid="search-results">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex gap-4 p-4">
                  <div className="w-20 h-24 skeleton rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-2">
              {results.map((product) => (
                <button
                  key={product.product_id}
                  onClick={() => handleProductClick(product.product_id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-stone-50 rounded-lg transition-colors text-left"
                  data-testid={`search-result-${product.product_id}`}
                >
                  <img
                    src={getImageUrl(product.images[0])}
                    alt={product.name}
                    className="w-16 h-20 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">
                      {product.collection}
                    </p>
                    <h3 className="font-light text-base text-stone-900 truncate">
                      {product.name}
                    </h3>
                    <p className="text-base font-light text-stone-900 mt-1">
                      {formatPrice(product.price)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : query ? (
            <div className="text-center py-12">
              <p className="text-stone-600">No products found for "{query}"</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-stone-600">Start typing to search...</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
